import React, { useEffect, useRef, forwardRef, useState, useImperativeHandle } from 'react';
import { Stage, Layer, Rect, Line, Group, Circle, Ellipse, Path } from 'react-konva';
import { degToRad, isNotEmpty, radToDeg } from '../../lib/ep';
import GameShooter from './GameShooter';

const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

const default_colors = [
	[[55, 170, 225], [30, 115, 185]],	// blue
	[[230, 40, 15], [180, 25, 15]],		// red
	[[35, 225, 100], [25, 150, 60]],	// green
	[[225, 15, 185], [180, 15, 150]],	// pink
	[[205, 205, 205], [150, 150, 150]],	// white
	[[20, 230, 225], [15, 185, 180]],	// turquoise
	[[235, 150, 20], [190, 120, 15]],	// orange
	[[230, 195, 30], [180, 150, 25]],	// yellow
	[[60, 60, 60], [30, 30, 30]],		// black
	[[135, 230, 40], [105, 175, 30]],	// lime
];

const Basket = ({ basket_props, ...props }) => {
	const cords_props = {
		stroke:'rgba(255, 255, 255, 1)',
		strokeWidth:1 / basket_props.scaleX,
		lineCap:'round',
		lineJoin:'round',
		fill:'transparent',
	};

	const shadow_props = {
		fill:'#1D1E1C',
		opacity:0.2,
	};

	const structure_props = {
		fill:'#E85B0D',
	};

	const glass_props = {
		stroke:'#C6C6C5',
		strokeWidth:2 / basket_props.scaleX,
		fill:'transparent',
	};

	return (
		<Group x={55}>
			<Group>
				<Line
					{...cords_props}
					points={[-18.33, 0, -9.65, 31.04]}
				/>

				<Line
					{...cords_props}
					points={[18.33, 0, 9.65, 31.04]}
				/>

				<Line
					{...cords_props}
					points={[-9.65, 31.04, 28.94, 77.01, 9.65, 100, -28.94, 54.03]}
					closed={true}
				/>

				<Line
					{...cords_props}
					points={[-9.65, 100, -28.94, 77.01, 9.65, 31.04, 28.94, 54.03]}
					closed={true}
				/>

				<Path
					{...cords_props}
					data="M55,0C39.3,18.7,28.94,34,28.94,54.03c0,7.42,0,45.97,0,45.97S-55,0-55,0c15.7,18.7,26.06,34,26.06,54.03c0,7.42,0,45.97,0,45.97S55,0,55,0z"
				/>
			</Group>

			<Path
				{...structure_props}
				data="M62.5,3h-125c-1.66,0-3-1.34-3-3s1.34-3,3-3h125c1.66,0,3,1.34,3,3S64.16,3,62.5,3z"
			/>

			<Line
				{...shadow_props}
				points={[12.5, 0.99, 16.82, 3, 31.08, 3]}
				closed={true}
			/>

			<Path
				{...shadow_props}
				data="M65.32-1H12.5l51.65-1.44C64.15-2.44,64.97-2.08,65.32-1z"
			/>

			<Path
				{...shadow_props}
				data="M-62.5,3h125l-50-2.01L-65.5,0C-65.5,1.66-64.16,3-62.5,3z"
			/>

			<Line
				{...structure_props}
				points={[92.5, -180, 92.5, -1, 12.5, -1, 12.5, 0.99, 92.5, 15, 92.5, 20, 102.5, 20, 102.5, -150, 237.5, -150, 237.5, -180]}
				closed={true}
			/>

			<Line
				{...shadow_props}
				points={[93.51, -180, 94.49, -178.55, 94.49, 20, 92.5, 20, 92.5, -180]}
				closed={true}
			/>

			<Line
				{...shadow_props}
				points={[12.5, 0.99, 92.5, 11.13, 92.5, 15]}
				closed={true}
			/>

			<Line
				{...glass_props}
				points={[92.5, 20, 92.5, -180]}
			/>

			<Line
				{...shadow_props}
				points={[102.5, -150, 102.5, -180, 133.45, -150]}
				closed={true}
			/>
		</Group>
	)
};

const Balloon = forwardRef(({ color, line_color, balloon_props, ...props }, ref) => {
	const [ is_popped, setIsPopped ] = useState(false);
	const [ is_visible, setIsVisible ] = useState(true);
	const [ fill_color, setFillColor ] = useState(color);
	const [ stroke_color, setStrokeColor ] = useState(line_color);

	useImperativeHandle(ref, () => ({
		pop() {
			setIsPopped(true);

			setTimeout(() => {
				setIsVisible(false);
			}, 200);
		},

		changeColors(fill_color, stroke_color) {
			setFillColor(fill_color);
			setStrokeColor(stroke_color);
		},

		reset() {
			setIsPopped(false);
			setIsVisible(true);
		},
	}), []);

	const generic_props = {
		lineCap:'round',
		lineJoin:'round',
	};

	const balloon_line_props = {
		...generic_props,
		strokeWidth:1 / balloon_props.scaleX,
		stroke:`rgba(${stroke_color.join(", ")}, 1)`,
	};

	const cord_props = {
		...generic_props,
		strokeWidth:2 / balloon_props.scaleX,
		stroke:'black',
	};

	const light_reflexion_props = {
		...generic_props,
		strokeWidth:4 / balloon_props.scaleX,
		stroke:'white',
		opacity:0.5,
	};

	const intense_light_reflexion_props = {
		...generic_props,
		strokeWidth:1.5 / balloon_props.scaleX,
		stroke:'white',
	};

	if(is_visible === false) return null;

	if(is_popped) {
		return (
			<>
				<Path
					fill={`rgba(${stroke_color.join(", ")}, 1)`}
					data="M65.12,17.1C51.88,9.61,50.78-20,50.78-20c-0.55,8.11-4.43,28.29-18.56,34.33C18.09,20.37,3.36,6.97,3.36,6.97c4.32,6.22,12.31,23.58,7.32,34.14c-4.99,10.56-21.99,5.66-21.99,5.66S8.28,56.58,8.28,76.39s-20.51,29.8-20.51,29.8s21.93-8.27,29.75,7.92c6.84,14.15-6.84,30.37-6.84,30.37s14.23-13.77,24.58-7.36c10.56,6.54,5.54,32.07,5.54,32.07s6.63-25.77,23.58-27.15c14.69-1.2,23.43,17.05,24.28,18.91c-1-2.23-12.39-28.32-4.25-40.05c8.5-12.26,29.38-3.77,29.38-3.77c-9.42-3.77-18.48-16.6-16.08-31.5c2.4-14.9,21.81-23.39,21.81-23.39c-14.23-0.57-20.7-0.94-28.27-13.77c-7.58-12.83,3.31-38.86,3.31-38.86S77.42,24.05,65.12,17.1z M91.39,12.36c0,0-12.52,26.25-4.94,39.08c7.58,12.83,15.27,12.08,29.5,12.64c0,0-19.42,4-22.95,21.07c-3.05,14.78,8.43,26.8,17.86,30.57c0,0-21.49-9.12-29.99,3.14c-8.5,12.26,3.36,32.62,3.36,32.62s-9.63-15.55-21.75-14.23c-15.44,1.67-19.66,23.01-19.66,23.01s3.16-24.03-6.14-28.26c-10.17-4.63-22.41,8.63-22.41,8.63s12.87-19.75,6.82-31c-6.8-12.65-27.32-6.43-27.32-6.43s18.67-9.22,18.67-26.05c0-19.9-18.95-28.56-18.95-28.56s17.68,4.14,21.1-5.27c3.73-10.25-3.23-25.9-7.75-32.66c0,0,12.88,13.27,27.48,8.57c6.81-2.19,13.66-14.31,14.22-22.42c0,0,1.6,16.47,14.84,23.95C75.7,27.74,91.39,12.36,91.39,12.36z"
				/>

				<Path
					fill={`rgba(${fill_color.join(", ")}, 0.8)`}
					data="M14.28,140.64c0,0,12.87-19.75,6.82-31c-6.8-12.65-27.32-6.43-27.32-6.43s18.67-9.22,18.67-26.05c0-19.9-18.95-28.56-18.95-28.56s17.68,4.14,21.1-5.27c3.73-10.25-3.23-25.9-7.75-32.66c0,0,12.88,13.27,27.48,8.57c6.81-2.19,13.66-14.31,14.22-22.42c0,0,1.6,16.47,14.84,23.95c12.31,6.95,28-8.43,28-8.43s-12.52,26.25-4.94,39.08c7.58,12.83,15.27,12.08,29.5,12.64c0,0-19.42,4-22.95,21.07c-3.05,14.78,8.43,26.8,17.86,30.57c0,0-21.49-9.12-29.99,3.14s3.36,32.62,3.36,32.62s-9.63-15.55-21.75-14.23c-15.44,1.67-19.66,23.01-19.66,23.01s3.16-24.03-6.14-28.26C26.52,127.38,14.28,140.64,14.28,140.64z"
				/>

				<Line
					fill={`rgba(${stroke_color.join(", ")}, 1)`}
					points={[93.61,126.73, 129.63,162.94, 126.84,165.61, 93.61,126.73]}
					closed={true}
				/>

				<Line
					fill={`rgba(${stroke_color.join(", ")}, 1)`}
					points={[114.15,71.4, 157.22,60.22, 158,64.04, 114.15,71.4]}
					closed={true}
				/>

				<Line
					fill={`rgba(${stroke_color.join(", ")}, 1)`}
					points={[98.14,44.83, 137.61,12.78, 139.88,15.93, 98.14,44.83]}
					closed={true}
				/>

				<Line
					fill={`rgba(${stroke_color.join(", ")}, 1)`}
					points={[114.15,14.6, 134.51,-9.44, 137.24,-6.71, 114.15,14.6]}
					closed={true}
				/>

				<Line
					fill={`rgba(${stroke_color.join(", ")}, 1)`}
					points={[0.95,29.96, -38.07,-3.71, -35.5,-6.59, 0.95,29.96]}
					closed={true}
				/>

				<Line
					fill={`rgba(${stroke_color.join(", ")}, 1)`}
					points={[-0.25,121.61, -39.69,154.62, -42,151.51, -0.25,121.61]}
					closed={true}
				/>

				<Line
					fill={`rgba(${stroke_color.join(", ")}, 1)`}
					points={[23.64,150.62, 11.74,180, 8.3,178.29, 23.64,150.62]}
					closed={true}
				/>

				<Path
					{...light_reflexion_props}
					data="M48.47,12.29c-3.55,5.86-8.38,10.36-12.61,11.72"
				/>

				<Path
					{...light_reflexion_props}
					data="M18.38,23.63c2.2,6.93,3.32,14.8,0.91,21.42c-0.56,1.55-1.65,3.58-3.76,5.37"
				/>

				<Path
					{...intense_light_reflexion_props}
					data="M46.99,14.55c-1.1,1.57-2.29,3-3.52,4.26"
				/>

				<Path
					{...intense_light_reflexion_props}
					data="M19.79,28.97c0.85,4.09,1.16,8.3,0.5,12.24"
				/>
			</>
		)
	}

	return (
		<>
			<Path
				{...cord_props}
				data="M50.91,164c-0.34,6.19-1.15,11.8-0.91,15.95c0.61,10.86,2.25,15.2,2,20.01c-0.35,6.69-2.84,12.54-2,20.01c0.59,5.23,5,13.67,5,20.01c0,12.07-10,18.29-10,30.01c0,8.08,5.3,22.71,5,30.01"
			/>

			<Path
				{...balloon_line_props}
				fill={`rgba(${fill_color.join(", ")}, 0.8)`}
				data="M50,0c27.73,0,50,20.19,50,60s-20.53,71.05-47,88c3.36,7.52,6.87,12.28,6.87,12.28S56.65,163,50,163s-9.87-2.72-9.87-2.72c0.44-0.6,4.85-5.23,7.87-12.28C21.53,131.05,0,99.81,0,60S22.27,0,50,0z"
			/>

			<Path
				{...cord_props}
				data="M46,148c3.61,1.26,8,1,8,1"
			/>

			<Path
				{...light_reflexion_props}
				data="M22.22,23.33c-7.6,8.74-9.72,21.41-9.72,32.82"
			/>

			<Path
				{...light_reflexion_props}
				data="M31.63,16.16c-0.89,0.43-1.75,0.9-2.56,1.39"
			/>

			<Path
				{...balloon_line_props}
				fill={`rgba(${fill_color.join(", ")}, 1)`}
				data="M50,162.27c5.92,0,8.78-2.82,8.89-2.93c0.52-0.54,1.39-0.56,1.93-0.04c0.54,0.52,0.56,1.38,0.04,1.93C60.71,161.38,57.15,165,50,165c-7.15,0-10.71-3.62-10.86-3.77c-0.52-0.54-0.5-1.41,0.04-1.93c0.54-0.52,1.41-0.5,1.93,0.04C41.22,159.46,44.08,162.27,50,162.27z"
			/>

			<Path
				{...intense_light_reflexion_props}
				data="M22.22,23.33c-2.77,3.18-4.81,6.89-6.28,10.86"
			/>

			<Path
				{...intense_light_reflexion_props}
				data="M31.63,16.16c-0.89,0.43-1.75,0.9-2.56,1.39"
			/>
		</>
	)
});

const Dard = ({ color_1, color_2, dard_props, ...props }) => {
	const generic_props = {
		lineCap:'round',
		lineJoin:'round',
	};

	const line_props = {
		...generic_props,
		strokeWidth:1 / dard_props.scaleX,
		stroke:'black',
	};

	const thin_line_props = {
		...line_props,
		strokeWidth:0.5 / dard_props.scaleX,
	};

	const metal_props = {
		...line_props,
		fill:'rgba(175, 175, 175, 1)',
	};

	return (
		<Group {...dard_props}>
			<Path
				{...line_props}
				fill={`rgba(${color_1.join(", ")}, 1)`}
				data="M-120,0l-19.05,16.91c-2.07,1.84-4.29,3.55-7.02,2.98l-29.08-5.97c-2.07-0.42-2.42-2.42-1-3.98l9.02-9.94l-9.02-9.94c-1.41-1.56-1.07-3.55,1-3.98l29.08-5.97c2.72-0.57,4.94,1.14,7.02,2.98L-120,0z"
			/>

			<Path
				fill={`rgba(${color_1.join(", ")}, 1)`}
				data="M-25,9l-158.38-7.31C-184.29,1.65-185,0.91-185,0s0.71-1.65,1.62-1.69L-25-9V9z"
			/>

			<Path
				fill={`rgba(${color_2.join(", ")}, 1)`}
				data="M-100-5.54l-83.38,3.85C-184.29-1.65-185-0.91-185,0s0.71,1.65,1.62,1.69L-100,5.54V-5.54z"
			/>

			<Line
				{...metal_props}
				points={[20, 0, -20, -4, -20, 4]}
				closed={true}
			/>

			<Path
				{...metal_props}
				data="M-25-9l10,2c2.14,0.43,3,1.81,3,4v6c0,2.19-0.86,3.57-3,4l-10,2V-9z"
			/>

			<Line
				{...line_props}
				points={[-25, -8.63, -25, 9.37]}
			/>

			<Line
				{...thin_line_props}
				points={[-20, -7.96, -20, 8.31]}
			/>

			<Path
				{...line_props}
				data="M-25,9l-158.38-7.31C-184.29,1.65-185,0.91-185,0s0.71-1.65,1.62-1.69L-25-9V9z"
			/>
		</Group>
	)
};

const Ball = ({ color_1, color_2, ball_props, ...props }) => {
	const size = props?.size ?? 50;

	const circle_props = {
		fill:`rgba(${color_1.join(", ")}, 1)`,
		radius:size,
	};

	const circle_out_props = {
		strokeWidth:1 / ball_props.scaleX,
		stroke:`rgba(${color_2.join(", ")}, 1)`,
		fill:'transparent',
		radius:size,
	};

	const line_props = {
		strokeWidth:1 / ball_props.scaleX,
		stroke:'black',
		fill:'transparent',
	};

	return (
		<Group {...ball_props}>
			<Circle
				{...circle_props}
			/>

			<Line
				{...line_props}
				points={[0, -25, 0, 25]}
			/>

			<Line
				{...line_props}
				points={[-25, 0, 25, 0]}
			/>

			<Path
				{...line_props}
				data="M15.71,19.45C13.9,10.92,6.41,7.86,6.41,0s7.48-10.92,9.29-19.45"
			/>

			<Path
				{...line_props}
				data="M-15.71,19.45C-13.9,10.92-6.41,7.86-6.41,0s-7.48-10.92-9.29-19.45"
			/>

			<Circle
				{...circle_out_props}
			/>
		</Group>
	)
};

const CannonballCanvas = forwardRef(({ stage, ...props }, ref) => {
	const mode = stage?.cannonball_mode ?? 'basket';
	const display_outter_border = stage?.display_outter_border ?? true;

	const indexed_images = props?.indexed_images ?? {};
	const bg_color = stage?.bg_color ?? 'rgba(80, 190, 195, 1)';
	const relocate_ball = stage?.cannonball_relocate_ball ?? 'fixed';
	const relocate_target = stage?.cannonball_relocate_target ?? 'random';
	const num_hits = parseInt(stage?.cannonball_num_hits ?? '1');
	const shooter_color = stage?.shooter_color ?? 'rgba(255, 255, 255, 1)';
	const obstacle_type = stage?.cannonball_obstacle_type ?? 'none';
	const relocate_obstacle = stage?.cannonball_relocate_obstacle ?? 'fixed';

	const target_move_duration = (relocate_target == 'semi-moving') ? 1 : 3;	// in seconds
	const cycle_target_delay = (relocate_target == 'semi-moving') ? 5 : 0;	// in seconds

	const obstacle_move_duration = 2;	// in seconds
	const cycle_obstacle_delay = 1;	// in seconds

	let obstacle_hole_size;
	switch(stage?.cannonball_obstacle_hole_size ?? 'normal') {
		case 'small':
			obstacle_hole_size = 0.2;
			break;

		case 'big':
			obstacle_hole_size = 0.4;
			break;

		case 'huge':
			obstacle_hole_size = 0.5;
			break;

		case 'normal':
		default:
			obstacle_hole_size = 0.3;
			break;
	}

	let floor_level_y = parseInt(stage?.cannonball_floor_y ?? '80');
	if(floor_level_y > 100) floor_level_y = 110;	// ensure the ball will disappear under the edge

	const gravity = -1200;
	const force_multiplier = 1.618 * Math.abs(gravity);
	const restrict_top = false;
	const restrict_sides = false;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const h_prop = 10 / 8;
	const default_size_w = 600;
	const default_size_h = default_size_w * h_prop;

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	
	const points_for_goal = 100;
	const points_for_fail = -1 * Math.abs(parseInt(stage?.cannonball_points_for_fail ?? '0'));

	const delay_to_success = 5; // in seconds
	const is_debug = true;

	const refs = useRef([]);
	const size = useRef({ w:default_size_w, h:default_size_h });
	const animations = useRef(false);
	const game_ended = useRef(false);
	const detector = useRef(false);
	const game_score = useRef(0);
	const ball_animation = useRef();
	const target_moving = useRef(false);
	const actual_target_id = useRef(0);
	const actual_goals = useRef(0);
	const is_playable = useRef((!is_builder || is_test) ? true : false);
	const floor_line_y = useRef(size.current.h * floor_level_y / 100);
	const obstacle_h = useRef(floor_line_y.current * 0.7);
	const obstacle_w = useRef(size.current.w / 8);

	const scenario_scale = size.current.h / 1000;
	const scenario_scale_w = size.current.w / 800;
	const ball_radius = 50 * scenario_scale_w;
	const dard_size = 30 * scenario_scale_w;

	const throw_size = (mode == 'basket') ? ball_radius : dard_size;

	const target_edge_delta = 30;

	const getBallCoords = () => {
		const x = 0.15 * size.current.w;

		if(relocate_ball == 'random') {
			return {
				x,
				y:Math.min(Math.max(0.3, Math.min(0.8, Math.random())) * size.current.h, floor_line_y.current) - 0.7 * throw_size,
			};
		}

		return {
			x,
			y:floor_line_y.current - 0.7 * throw_size,
		};
	};

	const getTargetSize = () => {
		if(mode == 'basket') {
			return {
				w:110,
				h:30,
			};
		}

		return {
			w:100,
			h:160,
		};
	};

	const getTargetCoords = () => {
		const x = size.current.w - target_size.current.w / 2 - target_edge_delta;

		if(relocate_target != 'random') {
			return {
				x,
				y:(stage?.cannonball_target_y ?? 50) * floor_line_y.current / 100 - 0.7 * throw_size,
			};
		}

		const max_y = (mode == 'basket') ? 0.45 : 0.8;

		return {
			x,
			y:Math.min(Math.max(0.3, Math.min(max_y, Math.random())) * size.current.h, floor_line_y.current) - 0.7 * throw_size,
		};
	};

	const getObstacleCoords = () => {
		const x = size.current.w / 2;

		if(obstacle_type == 'normal') {
			if(relocate_obstacle == 'fixed') {
				return {
					x,
					y:(stage?.cannonball_obstacle_y ?? 100) * floor_line_y.current / 100 - obstacle_h.current,
				};
			}

			if(relocate_obstacle == 'random') {
				return {
					x,
					y:floor_line_y.current - Math.random() * obstacle_h.current,
				};
			}
		}

		return {
			x,
			y:obstacle_h.current + floor_line_y.current * obstacle_hole_size,
		}
	};

	const ball_coords = useRef(getBallCoords());
	const target_size = useRef(getTargetSize());
	const target_coords = useRef(getTargetCoords());
	const obstacle_coords = useRef(getObstacleCoords());

	const reset_props = {
		duration:0.5,
		easing:Konva.Easings.BounceEaseOut,
	};

	/* SOUNDS - ini */
	const kickPlay = () => props.sound_fx.kick.play();
	const goalPlay = () => props.sound_fx.goal.play();
	const rejectPlay = () => props.sound_fx.reject.play();
	const rejectPlayerPlay = () => props.sound_fx.reject_player.play();
	const successPlay = () => props?.sound_fx?.success.play();
	const failPlay = () => props?.sound_fx?.fail.play();
	const extraGoalPlay = () => {
		if(props.sound_fx.extra_goal == undefined) return;
		props.sound_fx.extra_goal.play();
	};
	/* SOUNDS - end */

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};
	
	const resetGame = () => {
		setFullLoaded(false);
		if(refs.current['goal_keeper_player'] != undefined) {
			refs.current['goal_keeper_player'].stopWaiting();
		}

		animations.current = false;
		game_ended.current = false;
		detector.current = false;
		actual_goals.current = 0;
		game_score.current = 0;
	
		gameInit();
	};

	const generateCache = () => {
		/*if(refs.current['foosball_bars_keeper'] != undefined) {
			refs.current['foosball_bars_keeper'].cache(default_cache_props);
		}*/
	};

	const moveObstacles = (direction = 1) => {
		if(obstacle_type == 'none') return;
		if(relocate_obstacle != 'moving' && obstacle_type == 'normal') return;

		if(refs.current['obstacle_t'] != undefined) {
			refs.current['obstacle_t'].to({
				y:(direction > 0) ? 0 : -obstacle_h.current,
				duration:obstacle_move_duration,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		if(refs.current['obstacle_b'] == undefined) return;

		const final_y = (direction > 0) ? floor_line_y.current - obstacle_h.current : floor_line_y.current;

		refs.current['obstacle_b'].to({
			height:(direction > 0) ? obstacle_h.current : 0,
			y:final_y,
			duration:obstacle_move_duration,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					moveObstacles(-1 * direction);
				}, cycle_obstacle_delay * 1000);
			},
		});
	};

	const moveTarget = (direction = 1) => {
		if(!['semi-moving', 'moving'].includes(relocate_target)) return;
		target_moving.current = true;

		const new_y = target_coords.current.y + direction * (floor_line_y.current * 0.25);

		if(refs.current['target'] != undefined) {
			const top_props = {
				y:new_y,
				duration:target_move_duration,
				easing:Konva.Easings.StrongEaseInOut,
			};

			refs.current['target'].to({
				...top_props,
			});

			refs.current['target_visual'].to({
				...top_props,
				onFinish:() => {
					setTimeout(() => {
						if(target_moving.current === false) return;
						moveTarget(-1 * direction);
					}, cycle_target_delay * 1000);
				},
			});
		}
	};

	const mountGame = () => {
		if(refs.current['ball'] != undefined) {
			refs.current['ball'].setAttrs({
				opacity:1,
			});
		}

		if(is_playable.current === false) return;

		if(refs.current['whole_tapper'] != undefined) {
			refs.current['whole_tapper'].setAttrs({
				y:size.current.h + 10,
			});
		}

		moveObstacles();
		moveTarget();

		props.startTimer();
	};

	const gameInit = (from_resize = false) => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((_size) => {
				size.current = { w:_size, h:_size * h_prop };
				floor_line_y.current = size.current.h * floor_level_y / 100;
				obstacle_h.current = floor_line_y.current * 0.7;
				obstacle_w.current = size.current.w / 8;

				ball_coords.current = getBallCoords();
				target_size.current = getTargetSize();
				target_coords.current = getTargetCoords();
				obstacle_coords.current = getObstacleCoords();

				if(refs.current['ball'] != undefined) {
					refs.current['ball'].setAttrs({ ...ball_coords.current });
				}
				if(refs.current['obstacle_b'] != undefined) {
					refs.current['obstacle_b'].setAttrs({ ...obstacle_coords.current });
				}
				if(refs.current['shooter'] != undefined) {
					refs.current['shooter'].moveTo({ ...ball_coords.current });
				}

				if(!from_resize) {
					setTimeout(() => {
						generateCache();
						mountGame();
					}, 300);
				}

				setFullLoaded(true);
			});
		}
	};

	useEffect(() => {
		resetGame();
		generateCache();
	}, [
		stage.cannonball_floor_y,
		stage.cannonball_relocate_obstacle,
		stage.cannonball_obstacle_hole_size,
	]);

	useEffect(() => {
		window.onresize = () => {
			gameInit(true);
		}

		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		};
	}, []);

	const unmountBoardAnimation = (is_solved = true, cb) => {
		is_playable.current = false;

		if(refs.current['ball'] == undefined) return;

		(is_solved === false) ? failPlay() : successPlay();

		refs.current['ball_shadow'].to({
			opacity:0,
			duration:1.5,
		});

		refs.current['ball'].to({
			opacity:0,
			duration:1.5,
		});

		if(refs.current['whole_tapper'] != undefined) {
			refs.current['whole_tapper'].setAttrs({
				fill:(is_solved !== false) ? 'green' : 'red',
				y:0,
			});

			refs.current['whole_tapper'].to({
				opacity:0.7,
				duration:4,
				onFinish:() => {
					cb();
				},
			});

			return;
		}
	};

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			unmountBoardAnimation(success_game, () => {
				setTimeout(() => {
					if(success_game === false) success_game = 2;
					cb(success_game);
				}, (delay_to_success * 1000));
			});
		},
	}), []);

	if(!full_loaded) return null;

	const canvas_scale_w = size.current.w / default_size_w;
	const canvas_scale_h = size.current.h / default_size_h;

	const floor_y = floor_line_y.current - throw_size * canvas_scale_h;
	const wall_x = size.current.w - throw_size * canvas_scale_w;

	const getSpeedComponents = (speed, angle) => {
		return {
			vx:speed * Math.cos(angle),
			vy:speed * Math.sin(angle),
		};
	};

	const getMaxHeight = (speed, angle) => {
		// source: https://beaypepe.com/composicion-de-movimientos-tiro-parabolico/
		const { vy } = getSpeedComponents(speed, angle);
		return ball_coords.current.y + (Math.pow(vy, 2) / (2 * Math.abs(gravity)));
	};

	const getMaxLength = (speed, angle) => {
		// source: http://www.sc.ehu.es/sbweb/fisica_/cinematica/curvilineo/parabolico/parabolico1.html
		return (Math.pow(speed, 2) * Math.sin(-2 * angle)) / Math.abs(gravity);
	};

	const getParabollicSlope = (speed, angle, t) => {
		// source: https://www.ncl.ac.uk/webtemplate/ask-assets/external/maths-resources/mechanics/kinematics/projectiles.html
		const { vx, vy } = getSpeedComponents(speed, angle);
		return radToDeg(Math.atan((vy - gravity * t) / vx));
	};

	const getParabollicDuration = (speed, angle) => {
		// https://phys.libretexts.org/Bookshelves/University_Physics/Physics_(Boundless)/3%3A_Two-Dimensional_Kinematics/3.3%3A_Projectile_Motion
		const { vy } = getSpeedComponents(speed, angle);
		return 1.4 * (2 * vy / gravity);
	};

	const getParabollicPathCoords = (speed, angle, t) => {
		// source: https://beaypepe.com/composicion-de-movimientos-tiro-parabolico/
		const { vx, vy } = getSpeedComponents(speed, angle);
		return {
			x:ball_coords.current.x + vx * t,
			y:ball_coords.current.y + vy * t - gravity * Math.pow(t, 2) / 2,
		};
	};

	const updateScore = (is_goal = true) => {
		if((!is_builder || is_test) && props.doHit != undefined) props.doHit(checkSolved());

		game_score.current = Math.max(0, game_score.current + ((is_goal) ? points_for_goal : points_for_fail));
		if(is_goal) actual_goals.current = actual_goals.current + 1;
		if(props.onScore != undefined) props.onScore(game_score.current);
		if(props.updateActualGoals != undefined) props.updateActualGoals(actual_goals.current);

		isSolved();
	};

	const haveIntersection = (r1, r2) => {
		return !(
			r2.x > r1.x + r1.width ||
			r2.x + r2.width < r1.x ||
			r2.y > r1.y + r1.height ||
			r2.y + r2.height < r1.y
		);
	};

	const calcShadowScale = () => {
		return Math.min(1, Math.max(0.3, refs.current['ball'].attrs.y / ball_coords.current.y));
	};

	const resetTarget = (is_success = false) => {
		if(refs.current['target_visual'] == undefined) return;

		if(is_success) {
			actual_target_id.current++;
			if(actual_target_id.current >= default_colors.length) actual_target_id.current = 0;

			if(mode != 'basket') {
				refs.current['target_visual_c'].reset();

				refs.current['target_visual_c'].changeColors(default_colors[actual_target_id.current][0], default_colors[actual_target_id.current][1]);
			}

			if(relocate_target == 'random') target_coords.current = getTargetCoords();
		}

		refs.current['target'].to({
			x:target_coords.current.x,
			y:target_coords.current.y,
			duration:1.5,
			easing:Konva.Easings.StrongEaseInOut,
		});

		if(mode == 'basket') {
			if(refs.current['target_b'] != undefined) {
				refs.current['target_b'].to({
					x:target_coords.current.x,
					y:target_coords.current.y + target_props.height,
					duration:1.5,
					easing:Konva.Easings.StrongEaseInOut,
				});
			}

			if(refs.current['table'] != undefined) {
				refs.current['table'].to({
					y:target_coords.current.y - 130,
					duration:1.5,
					easing:Konva.Easings.StrongEaseInOut,
				});
			}
		}

		refs.current['target_visual'].to({
			x:target_coords.current.x,
			y:target_coords.current.y,
			duration:1.5,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const resetBall = (is_success = false) => {
		if(refs.current['ball'] == undefined) return;

		if(relocate_ball == 'random' && is_success) {
			ball_coords.current = getBallCoords();
		}

		resetTarget(is_success);
		
		if(relocate_obstacle == 'random' && is_success) {
			obstacle_coords.current = getObstacleCoords();

			refs.current['obstacle_b'].to({
				x:obstacle_coords.current.x,
				y:obstacle_coords.current.y,
				duration:1.5,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current['ball_cross'].setAttrs({
			opacity:0,
		});

		refs.current['ball_icon'].to({
			...reset_props,
			rotation:0,
		});

		refs.current['ball_shadow'].setAttrs({
			opacity:1,
			x:-100,
			scaleX:1,
			scaleY:1,
		});

		refs.current['ball'].setAttrs({
			opacity:1,
			x:-100,
			y:ball_coords.current.y,
			angle:0,
			collision_x:false,
			collision_y:false,
		});

		refs.current['ball_shadow'].to({
			x:ball_coords.current.x,
			duration:1.5,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['ball'].to({
			x:ball_coords.current.x,
			y:ball_coords.current.y,
			duration:1.5,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				if(refs.current['shooter'] == undefined) return;

				if(is_success) moveTarget();

				refs.current['shooter'].moveTo({
					y:ball_coords.current.y,
				});

				refs.current['shooter'].enableShooter();
			},
		});
	};

	const resetBallPosition = () => {
		if(refs.current['shooter'] != undefined) refs.current['shooter'].resetShooter(reset_props);

		if(refs.current['ball'] == undefined) return;

		refs.current['ball_icon'].to({
			...reset_props,
			rotation:0,
		});

		refs.current['ball'].to({
			...reset_props,
			x:ball_coords.current.x,
			y:ball_coords.current.y,
		});
	};

	const releaseShooter = (force, angle) => {
		if(is_playable.current === false) {
			refs.current['shooter'].resetShooter(reset_props);
			return;
		}

		if(force < 0.2 || angle > -5) { 
			resetBallPosition();
			return;
		}

		kickPlay();

		animations.current = true;

		refs.current['shooter'].resetShooter(reset_props);
		refs.current['shooter'].disableShooter();

		startAnimations();

		if(refs.current['ball_icon'] == undefined) return;

		const angle_in_rad = degToRad(-1 * (angle + 90));

		const duration = getParabollicDuration(force_multiplier * force, angle_in_rad);
		refs.current['shooter'].fadeTrajectory(duration);

		if(mode == 'basket') {
			refs.current['ball_icon'].to({
				rotation:1.3 * 360,
				duration:0.7 * duration,
			});
		}

		ball_animation.current = new Konva.Animation((frame) => {
			const time = frame.time / 1000;
			const coords = getParabollicPathCoords(force_multiplier * force, angle_in_rad, time);

			if(is_debug) {
				refs.current['ball_guide_y'].setAttrs({
					y:refs.current['ball'].attrs.y,
				});

				refs.current['ball_guide_x'].setAttrs({
					x:refs.current['ball'].attrs.x,
				});
			}

			if(coords.y >= floor_y && time > 0.2) {
				ball_animation.current.stop();

				refs.current['ball_shadow'].to({
					opacity:0,
					duration:1,
					easing:Konva.Easings.StrongEaseInOut,
				});

				refs.current['ball'].to({
					opacity:0,
					duration:1,
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						resetBall();
					},
				});
			}

			if(restrict_top) coords.y = Math.abs(coords.y);
			coords.y = Math.min(floor_y, coords.y);

			if(restrict_sides) {
				coords.x = (coords.x < wall_x) ? coords.x : Math.max(0, Math.abs((size.current.w - (coords.x - wall_x))));
			} else if(coords.x > 1.2 * size.current.w) {
				ball_animation.current.stop();
				coords.y = floor_y;
				resetBall();
			}

			const shadow_scale = calcShadowScale();

			if(mode != 'basket') {
				refs.current['ball_icon'].setAttrs({
					rotation:getParabollicSlope(force_multiplier * force, angle_in_rad, time),
				});
			}

			refs.current['ball'].setAttrs({ ...coords });
			refs.current['ball_shadow'].setAttrs({
				x:coords.x,
				scaleX:shadow_scale,
				scaleY:shadow_scale,
			});

			refs.current['layer'].draw();
		});

		ball_animation.current.start();
	};

	const moveBall = (shooter_props) => {
		if(refs.current['ball'] == undefined) return;

		refs.current['ball'].setAttrs({
			x:ball_coords.current.x + shooter_props.x,
			y:ball_coords.current.y + shooter_props.y,
		});
	};

	const rejectBall = () => {
		if(refs.current['ball'] == undefined) return;

		ball_animation.current.stop();

		refs.current['ball'].setAttrs({
			collision_x:refs.current['ball'].x(),
			collision_y:refs.current['ball'].y(),
		});

		refs.current['ball_cross'].to({
			opacity:1,
			duration:1.5,
			easing:Konva.Easings.StrongEaseOut,
		});

		refs.current['ball_shadow'].to({
			x:refs.current['ball'].attrs.collision_x - size.current.w / 5,
			scaleX:1,
			scaleY:1,
			duration:1.5,
			easing:Konva.Easings.StrongEaseOut,
		});

		refs.current['ball'].to({
			x:refs.current['ball'].attrs.collision_x - size.current.w / 5,
			y:floor_y,
			duration:1.5,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:() => {
				setTimeout(() => {
					if(refs.current['ball'] == undefined) return;

					const fade_props = {
						opacity:0,
						duration:0.8,
						easing:Konva.Easings.StrongEaseOut,
					};

					refs.current['ball_shadow'].to({
						...fade_props,
					});

					refs.current['ball_cross'].to({
						...fade_props,
					});

					refs.current['ball'].to({
						...fade_props,
						onFinish:() => {
							setTimeout(() => {
								resetBall();
							}, 800);
						},
					});
				}, 300);
			},
		});
	};

	const fallingBall = (init_y) => {
		if(refs.current['ball'] == undefined) return;

		const first_anim_props = {
			x:target_coords.current.x,
			duration:0.2,
			//easing:Konva.Easings.StrongEaseOut,
		};

		const second_anim_props = {
			duration:1.2,
			easing:Konva.Easings.BounceEaseOut,
		};

		refs.current['ball_shadow'].to({
			...first_anim_props,
			onFinish:() => {
				if(refs.current['ball_shadow'] == undefined) return;

				refs.current['ball_shadow'].to({
					scaleX:1,
					scaleY:1,
					...second_anim_props,
				});
			},
		});

		refs.current['ball'].to({
			y:init_y + 20,
			...first_anim_props,
			onFinish:() => {
				if(refs.current['ball'] == undefined) return;

				refs.current['ball'].to({
					y:floor_line_y.current,
					...second_anim_props,
					onFinish:() => {
						setTimeout(() => {
							resetBall(true);
						}, 1000);
					},
				});
			},
		});

	};

	const detectCollisions = () => {
		if(refs.current['obstacles'] == undefined || animations.current === false) return;
		
		if(refs.current['obstacles'].getChildren().length == 0) {
			if(detector.current !== false) requestAnimationFrame(detectCollisions);
			return;
		}

		refs.current['obstacles'].getChildren().map((item) => {
			const type = item.attrs.type;
			if(type == undefined) return;

			if(animations.current === false) return;
			if(refs.current['ball'].x() < refs.current['limit_x'].x()) return;

			if(haveIntersection(item.getClientRect(), refs.current['ball_target'].getClientRect())) {
				if(item.attrs.alias != undefined) {
					refs.current[item.attrs.alias].to({
						opacity:1,
						duration:0.5,
						easing:Konva.Easings.BounceEaseOut,
					});
				}

				if(['table'].includes(type)) {
					rejectBall();
					return;
				}

				animations.current = false;
				detector.current = false;

				if(['goal'].includes(type)) {
					const intersection_y = refs.current['ball'].attrs.y;

					target_moving.current = false;
					ball_animation.current.stop();
					updateScore(true);

					if(mode != 'basket') {
						refs.current['ball'].setAttrs({
							opacity:0,
						});

						refs.current['ball_shadow'].setAttrs({
							opacity:0,
						});

						refs.current['target_visual_c'].pop();

						setTimeout(() => {
							resetBall(true);
						}, 2000);
						return;
					}

					fallingBall(intersection_y);
					return;
				}

				updateScore(false);

				if(['obstacle_t', 'obstacle_b', 'goal_b'].includes(type)) {
					rejectBall();
					return;
				}
			}
		});

		if(detector.current !== false) requestAnimationFrame(detectCollisions);
	};

	const updateMaxCoords = (force, angle) => {
		const angle_ok = -1 * (angle + 90);

		const angle_in_rad = degToRad(angle_ok);
		const max_y = getMaxHeight(force_multiplier * force, angle_in_rad);
		const max_x = getMaxLength(force_multiplier * force, angle_in_rad);

		refs.current['ball_icon'].setAttrs({
			rotation:angle_ok,
		});

		const points = [0, 0, max_x / 2, ball_coords.current.y - max_y, max_x, 0];
		refs.current['shooter'].updateTrajectory(points);
	};

	const startAnimations = () => {
		game_ended.current = false;
		animations.current = true;

		detector.current = window.requestAnimationFrame(detectCollisions);
	};

	const checkSolved = () => {
		return (actual_goals.current < num_hits) ? false : true;
	};

	const isSolved = () => {
		let is_solved = checkSolved();

		if(is_solved) {
			successPlay();

			if(props.onEnded != undefined) props.onEnded(true);
		}

		return is_solved;
	};

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	const ball_scale = 0.65;

	const target_props = {
		width:target_size.current.w,
		height:target_size.current.h,
		offsetX:target_size.current.w / 2,
		offsetY:target_size.current.h / 2,
		x:target_coords.current.x,
		y:target_coords.current.y,
	};

	const bg_image = (isNotEmpty(indexed_images, 'bg_image')) ? indexed_images.bg_image : false;
	const bg_image_scale = (bg_image !== false) ? (size.current.w / bg_image.image_w) : 1;

	const obstacle_b_image = (isNotEmpty(indexed_images, 'obstacle_b')) ? indexed_images.obstacle_b : false;
	const obstacle_b_scale = (obstacle_b_image !== false) ? (obstacle_w.current / obstacle_b_image.image_w) : 1;

	const obstacle_visible_h = (relocate_obstacle == 'fixed') ? 0.2 : 0;

	return (
		<Stage
			ref={(node) => { refs.current['stage'] = node; }}
			class={main_classes.join(" ")}
			width={size.current.w}
			height={size.current.h}
		>
			<Layer ref={(node) => { refs.current['layer'] = node; }}>
				<Group
					ref={(node) => { refs.current['bg'] = node; }}
				>
					<Rect
						width={size.current.w}
						height={size.current.h}
						fill={bg_color}
					/>

					{(bg_image !== false) && (
						<Rect
							width={size.current.w}
							height={size.current.h}
							fillPatternImage={bg_image.fillPatternImage}
							fillPatternScaleX={bg_image_scale}
							fillPatternScaleY={bg_image_scale}
							//fillPatternScaleY={bg_image_scale}
							//fillPatternOffsetX={(scaleX_img < 0) ? img_size.w / bg_image_scale : 0}
						/>
					)}
				</Group>

				{(is_builder && !is_test) && (
					<Line
						points={[ 0, 0, size.current.w, 0 ]}
						y={floor_line_y.current}
						strokeWidth={1}
						stroke='black'
						opacity={0.2}
					/>
				)}

				{(is_debug) && (
					<Line
						ref={(node) => { refs.current['ball_guide_y'] = node; }}
						points={[ 0, 0, size.current.w, 0 ]}
						y={-10}
						strokeWidth={1}
						stroke='orange'
						opacity={0.5}
					/>
				)}

				{(is_debug) && (
					<Line
						ref={(node) => { refs.current['ball_guide_x'] = node; }}
						points={[ 0, 0, 0, size.current.h ]}
						x={-10}
						strokeWidth={1}
						stroke='orange'
						opacity={0.5}
					/>
				)}

				<Ellipse
					ref={(node) => { refs.current['ball_shadow'] = node; }}
					radius={{
						x:throw_size * ball_scale * canvas_scale_h,
						y:throw_size * ball_scale * canvas_scale_h * 0.5,
					}}
					x={ball_coords.current.x}
					y={floor_line_y.current + throw_size * ball_scale * canvas_scale_h * 0.5}
					fill='rgba(0, 0, 0, 0.2)'
				/>

				<Group
					ref={(node) => { refs.current['obstacles'] = node; }}
				>
					<Rect
						ref={(node) => { refs.current['target'] = node; }}
						{...target_props}
						scaleX={scenario_scale_w}
						scaleY={scenario_scale_w}
						fill='yellow'
						opacity={(is_debug) ? 0.5 : 0}
						type='goal'
					/>

					{(false && mode == 'basket') && (
						<Rect
							ref={(node) => { refs.current['target_b'] = node; }}
							{...target_props}
							scaleX={scenario_scale_w}
							scaleY={scenario_scale_w}
							y={target_props.y + target_props.height}
							fill='red'
							opacity={(is_debug) ? 0.5 : 0}
							type='goal_b'
						/>
					)}

					{(mode == 'basket') && (
						<Rect
							ref={(node) => { refs.current['table'] = node; }}
							width={10}
							height={200}
							x={(target_coords.current.x + 45)}
							y={(target_coords.current.y - 130)}
							scaleX={scenario_scale_w}
							scaleY={scenario_scale_w}
							fill='red'
							opacity={(is_debug) ? 0.5 : 0}
							type='table'
						/>
					)}

					{(obstacle_type == 'hole') && (
						<Rect
							ref={(node) => { refs.current['obstacle_t'] = node; }}
							width={obstacle_w.current}
							height={obstacle_h.current}
							x={obstacle_coords.current.x}
							y={0}
							offsetX={obstacle_w.current / 2}
							fill='red'
							opacity={(is_debug || true) ? 0.5 : 0}
							type='obstacle_t'
						/>
					)}

					{(obstacle_type != 'none') && (
						<Rect
							ref={(node) => { refs.current['obstacle_b'] = node; }}
							width={obstacle_w.current}
							height={obstacle_h.current * obstacle_visible_h}
							x={obstacle_coords.current.x}
							y={floor_line_y.current - obstacle_h.current * obstacle_visible_h}
							offsetX={obstacle_w.current / 2}
							fillPatternImage={obstacle_b_image.fillPatternImage}
							fillPatternScaleX={obstacle_b_scale}
							fillPatternScaleY={obstacle_b_scale}
							fillPatternRepeat='no-repeat'
							type='obstacle_b'
						/>
					)}

					<Line
						ref={(node) => { refs.current['limit_x'] = node; }}
						x={((obstacle_type == 'none') ? target_props.x : size.current.w / 2) - size.current.w / 7}
						points={[0, 0, 0, size.current.h]}
						strokeWidth={1}
						stroke='white'
						opacity={(is_debug) ? 0.5 : 0}
					/>

					<Group
						ref={(node) => { refs.current['ball'] = node; }}
						x={ball_coords.current.x}
						y={ball_coords.current.y}
						angle={0}
						collision_x={false}
						collision_y={false}
					>
						<Group
							ref={(node) => { refs.current['ball_icon'] = node; }}
							rotation={0}
						>
							{(mode == 'basket') && (
								<Ball
									size={throw_size}
									ball_props={{
										scaleX:ball_scale * canvas_scale_h,
										scaleY:ball_scale * canvas_scale_h,
									}}
									color_1={[240, 145, 0]}
									color_2={[230, 80, 25]}
								/>
							)}

							{(mode != 'basket') && (
								<Dard
									dard_props={{
										scaleX:0.5 * ball_scale * canvas_scale_h,
										scaleY:0.5 * ball_scale * canvas_scale_h,
									}}
									color_1={[220, 100, 20]}
									color_2={[140, 50, 160]}
								/>
							)}
						</Group>

						<Line
							ref={(node) => { refs.current['ball_cross'] = node; }}
							points={[30, -21.43, 21.43, -30, 0, -8.57, -21.43, -30, -30, -21.43, -8.57, 0, -30, 21.43, -21.43, 30, 0, 8.57, 21.43, 30, 30, 21.43, 8.57, 0]}
							closed={true}
							fill='red'
							scaleX={0.75 * ball_scale}
							scaleY={0.75 * ball_scale}
							opacity={0}
						/>

						<Rect
							ref={(node) => { refs.current['ball_target'] = node; }}
							width={1 * throw_size * canvas_scale_h}
							height={1 * throw_size * canvas_scale_h}
							offsetX={0.5 * throw_size * canvas_scale_h}
							offsetY={0.5 * throw_size * canvas_scale_h}
							fill='purple'
							opacity={(is_debug) ? 0.5 : 0}
						/>
					</Group>
				</Group>

				<Group
					ref={(node) => { refs.current['target_visual'] = node; }}
					{...target_props}
					scaleX={scenario_scale_w}
					scaleY={scenario_scale_w}
				>
					{(mode == 'basket') && (
						<Group
						>
							<Basket
								ref={(node) => { refs.current['target_visual_c'] = node; }}
								basket_props={{
									scaleX:scenario_scale_w,
									scaleY:scenario_scale_w,
								}}
							/>
						</Group>
					)}

					{(mode != 'basket') && (
						<Balloon
							ref={(node) => { refs.current['target_visual_c'] = node; }}
							color={default_colors[actual_target_id.current][0]}
							line_color={default_colors[actual_target_id.current][1]}
							balloon_props={{
								scaleX:scenario_scale_w,
								scaleY:scenario_scale_w,
							}}
						/>
					)}
				</Group>

				<GameShooter
					ref={(node) => { refs.current['shooter'] = node; }}
					shooter_props={{
						...ball_coords.current,
						scaleX:ball_scale * canvas_scale_h,
						scaleY:ball_scale * canvas_scale_h,
					}}
					color={shooter_color}
					game_display_path={stage?.game_display_path ?? 'full'}
					main_stage_axis={size.current.h}
					apply_heisenberg='no'
					onStart={moveBall}
					onDragMove={updateMaxCoords}
					onRelease={releaseShooter}
					is_debug={is_debug}
					custom_trajectory={true}
				/>

				{(!is_builder || is_test) && (
					<Rect
						ref={(node) => { refs.current['whole_tapper'] = node; }}
						y={0}
						width={size.current.w}
						height={size.current.h}
						fill='white'
						opacity={0}
					/>
				)}
			</Layer>
		</Stage>
	);
});

export default CannonballCanvas;